// 用户权限管理

import mainServerClient from "@/services/http/mainServerClient";
import Authority from "@/enum/Authority";
import UserDTO, { UserStatus } from "@/dto/user/UserDTO";
import { get } from "../http/platformServer";
import { PagedResult } from "./types";

/**
 * 获取当前用户对应店铺的所有子账号
 */
export function getAllUsers() {
    return mainServerClient.get<UserDTO[]>("/user/list");
}

export interface ShopNicknameMapping {
    /**
     * 店铺ID
     */
    shopId?: number;

    /**
     * username和 nickname映射列表
     */
    nicknameMappings?: {
        /**
         * 用户昵称
         */
        nickname?: string;
        /**
         * 用户名：用户唯一标识
         */
        username?: string;
    }[];
}

/**
 * 获取对应店铺的所有用户的索引
 */
export function getAllShopUsersMappings() {
    return mainServerClient.get<ShopNicknameMapping[]>("/user/get_nickname_mapping");
}

/**
 * 获取当前用户所在店铺的所有User（从ec-oms-node获取）
 */
export function getShopUsers(shopId: any): Promise<UserDTO[]> {
    return get(shopId, "/user");
}

// 获取用户
export function getUser(userId: any): Promise<UserDTO> {
    return mainServerClient.get(`/user/${userId}`);
}

// 更新用户权限
export function updateAuthorities(userId: any, authorityNames: any) {
    return mainServerClient.post(`user/${userId}:updateAuthorities`, authorityNames);
}

interface AddWeiXinUserParams {
    authorities: Authority[];
    affiliateMarketerOriginalId?: string;
    userName: string;
}

// 添加微信子账号
export function AddWeiXinUser(params: AddWeiXinUserParams): Promise<{ userId: number }> {
    return mainServerClient.post("/user/wechat/sub_user", params);
}

export function unBindWeChat(params: { userId: number }): Promise<{ userId: number }> {
    return mainServerClient.post("/user/wechat/sub_user/unbind", params);
}

export function bindAffiliateMarketer(userId: number, affiliateMarketerOriginalId: string) {
    return mainServerClient.post("/user/wechat/sub_user/bind_affiliate_marketer", {
        userId,
        affiliateMarketerOriginalId,
    });
}

export interface LoginResult {
    id: number;

    ip: string;

    loginTime: string;

    userId: number;
}

interface LoginRecordPageResult {
    items: LoginResult[];

    totalCount: number;
}

interface UserLoginRecordListReturn {
    loginRecord: LoginRecordPageResult;

    username: string;
}

// 登录记录
export function userLoginRecordList(pageNo: number, pageSize: number, userId): Promise<UserLoginRecordListReturn> {
    return mainServerClient.get("/login_record/list", { pageNo, pageSize, userId });
}

/**
 * 用户操作日志，记录子账号权限变更、禁用、恢复、删除等操作。
 *
 * UserOperationLog
 */
export interface UserOperationLog {
    /**
     * 公司 ID，仅用于排查问题，不参与业务查询。
     */
    companyId?: number;
    /**
     * 操作时间
     */
    dateCreated?: string;
    id?: number;
    /**
     * 操作者 IP
     */
    ip?: string;
    /**
     * 操作详情，权限变更时存储权限中文名（空格分隔）
     */
    operationDetail?: string;
    /**
     * 操作类型
     */
    operationType?: OperationType;
    /**
     * 操作者用户 ID
     */
    operatorUserId?: number;
    /**
     * 操作者用户名
     */
    operatorUsername?: string;
    /**
     * 店铺 ID，业务查询维度。
     */
    shopId?: number;
    /**
     * 被操作的子账号用户 ID
     */
    targetUserId?: number;
    /**
     * 被操作的子账号用户名
     */
    targetUsername?: string;

    message: string;
}

/**
 * 操作类型
 */
export enum OperationType {
    DeleteUser = "DELETE_USER",
    DisableUser = "DISABLE_USER",
    GrantAuthority = "GRANT_AUTHORITY",
    RecoverUser = "RECOVER_USER",
    RevokeAuthority = "REVOKE_AUTHORITY",
}

export function userOperationLogList(pageNo: number, pageSize: number, userId) {
    return mainServerClient.get<PagedResult<UserOperationLog>>(`/user/${userId}/operation_log`, {
        pageNo,
        pageSize,
    });
}

export function changeUserStatus(id: number, status: UserStatus) {
    return mainServerClient.post("/user/change_status", { status, id });
}

interface UserRegisterResult {
    successful: boolean;
    message: string;
}

/**
 * 手机号是否注册
 */
export function isUserRegister(mobile: string): Promise<UserRegisterResult> {
    return mainServerClient.get("/user/is_register", { mobile });
}

export interface UserNickname {
    userId: number;

    userNickname: string;

    username: string;
}

/** 批量获取用户名称，接口会对userId列表的id进行去重处理 */
export function batchGetUsername(userIds: number[]): Promise<UserNickname[]> {
    return mainServerClient.post("/user/batch_get_username", { userIds });
}

export interface WechatUserBinding {
    dateCreated: string;
    id: number;
    nickname: string;
    userId: number;
    username: string;
}

/** 列出当前用户所在店铺的所有微信绑定 */
export function getUserListWeChatBinding(): Promise<WechatUserBinding[]> {
    return mainServerClient.get("/user/list_wechat_user_binding");
}

/** 获取绑定微信回调的state */
export function getBindWeChatState(bindingUserId: number): Promise<{ state: string }> {
    return mainServerClient.get("/user/wechat/binding/state", { bindingUserId });
}

export async function getClientIp() {
    return mainServerClient.get<string>("/maintest/get_ip", undefined, {
        hideModal: true,
    });
}

/** 更改绑定的手机号相关接口  */

/** 发送验证码到原手机号（第一步）。 */
export async function sendCodeToOldPhone(captchaKey: string, captchaValue: string) {
    return mainServerClient.post<{ changePhoneToken: string }>("/user/change_phone/send_code_to_old", {
        captchaKey,
        captchaValue,
    });
}

/** 验证原手机号验证码（第二步）。 */
export async function verifyOldPhoneCode(oldPhone: string, smsCode: string, changePhoneToken: string) {
    return mainServerClient.post("/user/change_phone/verify_old_code", {
        oldPhone,
        smsCode,
        changePhoneToken,
    });
}

/** 发送验证码到新手机号（第三步）。 */
export async function sendCodeToNewPhone(
    captchaKey: string,
    captchaValue: string,
    newPhone: string,
    changePhoneToken: string,
) {
    return mainServerClient.post("/user/change_phone/send_code_to_new", {
        captchaKey,
        captchaValue,
        newPhone,
        changePhoneToken,
    });
}

/** 完成手机号更换（第四步）。 */
export async function changeNewPhone(newPhone: string, smsCode: string, changePhoneToken: string) {
    return mainServerClient.post("/user/change_phone/change", {
        newPhone,
        smsCode,
        changePhoneToken,
    });
}

export interface AffiliateMarketerUserBinding {
    /**
     * 带货达人名称
     */
    affiliateMarketerName: string;
    /**
     * 带货达人原始 ID（对应 AffiliateMarketer.originalId）
     */
    affiliateMarketerOriginalId: string;
    /**
     * 创建时间
     */
    dateCreated: string;
    /**
     * 最后更新时间
     */
    lastUpdated: string;
    /**
     * 用户 ID，对应 User.id，作为主键。
     */
    userId: number;

    /** 前端赋值 */
    shopId: number;
}

export function getAffiliateMarketerBindingList() {
    return mainServerClient.get<AffiliateMarketerUserBinding[]>("/user/list_affiliate_marketer_user_binding");
}

export default {
    getAllUsers,
    getShopUsers,
    getUser,
    updateAuthorities,
    AddWeiXinUser,
    unBindWeChat,
};
